"use client";

import { useTranslations, useLocale } from "next-intl";
import React, { useEffect } from "react";

interface LoanTenureSelectorProps {
  carYear: number;
  selectedYear?: number;
  onChange?: (year: number) => void;
}

const LoanTenureSelector: React.FC<LoanTenureSelectorProps> = ({ carYear, selectedYear, onChange }) => {
  const t = useTranslations("CarDetails.loanTenure");
  const locale = useLocale();
  const isRTL = locale === "ar";

  const carAge = new Date().getFullYear() - carYear;
  const getMaxAllowedYears = (year: number): number => {
    if (year >= 2018) return 5;
    if (year === 2017) return 4;
    if (year === 2016) return 3;
    return 1; // For cars older than 6 years
  };

  const yearOptions = [1, 2, 3, 4, 5];
  const maxAllowedYears = getMaxAllowedYears(carYear);

  const isDisabled = (year: number): boolean => {
    return year > maxAllowedYears;
  };

  const handleYearClick = (year: number) => {
    if (!isDisabled(year)) {
      onChange?.(year);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, year: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleYearClick(year);
    }
  };

  // Effect to select highest available tenure if current selection is disabled
  useEffect(() => {
    if (!selectedYear || isDisabled(selectedYear)) {
      onChange?.(maxAllowedYears);
    }
  }, [carYear, maxAllowedYears, selectedYear, onChange]);

  return (
    <div>
      <h4 className="mt-5 font-medium">{t("title")}</h4>
      <div className={`mt-4 flex w-full ${isRTL ? "space-x-3 space-x-reverse" : "space-x-3"}`}>
        {yearOptions.map((year) => (
          <button
            key={year}
            type="button"
            onClick={() => handleYearClick(year)}
            onKeyDown={(e) => handleKeyDown(e, year)}
            disabled={isDisabled(year)}
            className={`flex h-14 w-14 cursor-pointer items-center justify-center rounded-xl font-inter text-base font-bold transition-colors ${
              isDisabled(year)
                ? "cursor-not-allowed bg-gray-200 text-gray-400"
                : selectedYear === year
                  ? "bg-primary-light text-primary"
                  : "bg-secondary hover:bg-primary-light hover:text-primary"
            } `}
            aria-label={`${year} ${year === 1 ? "year" : "years"}`}
            aria-pressed={selectedYear === year}
          >
            {year}
          </button>
        ))}
      </div>
      <div className="mt-2 text-sm text-gray-500">
        {carAge > 3 && t("maxTenureMessage", { carAge, maxYears: maxAllowedYears })}
      </div>
    </div>
  );
};

export default LoanTenureSelector;
